import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { forkJoin, map, Observable, of } from 'rxjs';
import { Rule } from '../models/rule.model';
import { CreateRuleRequest } from '../models/requests/create-rule.request';
import { UpdateRuleRequest } from '../models/requests/update-rule.request';
import { UpdateRuleTrackedRequest } from '../models/requests/update-rule-tracked.request';
import { RuleStatistics } from '../models/rule-statistics.model';
import { AlarmStatistics } from '../models/alarm-statistics.model';
import { AlarmDistributionData } from '../models/alarm-distribution.model';
import { StageDistribution } from '../models/stage-distribution.model';
import { RulesGroupedByWorkCenterLabel } from '../models/rules-grouped-by-work-center-label.model';

const LAMBDA_API = 'LambdaApi';

interface DateChunk {
  from: Date;
  to: Date;
}

@Injectable({
  providedIn: 'root',
})
export class RuleService {
  constructor(private apiService: ApiService) {}

  public GetByUid(uniqueId: string): Observable<Rule> {
    const options = {
      queryParams: {
        uniqueId: uniqueId,
      },
    };
    return this.apiService.get<Rule>(`/Rule/GetByUid`, options);
  }

  public CreateRule(createRuleRequest: CreateRuleRequest): Observable<any> {
    return this.apiService.post(`/Rule/CreateRule`, createRuleRequest);
  }

  public UpdateRule(updateRuleRequest: UpdateRuleRequest): Observable<Rule> {
    return this.apiService.put<Rule>(`/Rule/UpdateRule`, updateRuleRequest);
  }

  public UpdateTracked(
    updateRuleTrackedRequest: UpdateRuleTrackedRequest,
  ): Observable<Rule> {
    return this.apiService.post<Rule>(
      `/Rule/UpdateTracked`,
      updateRuleTrackedRequest,
    );
  }

  public DropRule(ruleUid: string): Observable<any> {
    return this.apiService.delete<Rule>(`/Rule/${ruleUid}`);
  }

  public GetPaged(
    eventRequest: any,
  ): Observable<{ items: Rule[]; totalRecords: number }> {
    return this.apiService.post<{ items: Rule[]; totalRecords: number }>(
      `/Rule/GetPagedWithLatestAlarm`,
      eventRequest,
    );
  }

  public GetRuleStats(eventRequest?: any): Observable<RuleStatistics> {
    if (eventRequest) {
      return this.apiService.post<RuleStatistics>(
        `/Rule/GetRuleStats`,
        eventRequest,
      );
    }
    return this.apiService.get<RuleStatistics>(`/Rule/GetRuleStats`);
  }

  public GetAlarmStats(eventRequest?: any): Observable<AlarmStatistics> {
    if (eventRequest) {
      return this.apiService.post<AlarmStatistics>(
        `/Rule/GetAlarmStats`,
        eventRequest,
      );
    }
    return this.apiService.get<AlarmStatistics>(`/Rule/GetAlarmStats`);
  }

  public GetAlarmDistributionBySector(): Observable<AlarmDistributionData> {
    return this.apiService.get<AlarmDistributionData>(
      `/Rule/GetAlarmDistributionBySector`,
    );
  }

  public GetStageDistribution(
    eventRequest: any,
  ): Observable<StageDistribution> {
    return this.apiService.post<StageDistribution>(
      `/Rule/GetStageDistribution`,
      eventRequest,
    );
  }

  public GetGroupedByWorkCenterLabel(
    eventRequest: any,
  ): Observable<RulesGroupedByWorkCenterLabel[]> {
    return this.apiService.post<RulesGroupedByWorkCenterLabel[]>(
      `/Rule/GetRulesGroupedByWorkCenterLabel`,
      eventRequest,
    );
  }

  public RuleTester(requestBody: any): Observable<any> {
    return this.apiService.post(
      `/rule-tester`,
      requestBody,
      undefined,
      false,
      LAMBDA_API,
    );
  }

  /**
   * Calls the rule tester with chunking support for long date ranges.
   * Splits both the date range and the RulePiPointValues data into 2-week chunks
   * to avoid Lambda 413 errors caused by oversized request payloads.
   */
  public RuleTesterChunked(requestBody: any): Observable<any> {
    const fromDate = new Date(requestBody.from_date_time);
    const toDate = new Date(requestBody.to_date_time);

    const chunks = this.splitDateRangeForRuleTester(fromDate, toDate);

    // If only one chunk, use the regular non-chunked method
    if (chunks.length <= 1) {
      return this.RuleTester(requestBody);
    }

    // Create a request for each chunk, slicing RulePiPointValues to only the chunk's timestamps
    // Skip chunks where no timestamps have complete data for all PI points
    const chunkRequests: Observable<any>[] = chunks
      .map((chunk) => {
        const slicedValues = this.sliceRulePiPointValues(
          requestBody.RulePiPointValues,
          chunk.from,
          chunk.to,
        );
        if (!slicedValues?.ts?.length) {
          return null;
        }
        const chunkBody = {
          ...requestBody,
          from_date_time: chunk.from.toISOString(),
          to_date_time: chunk.to.toISOString(),
          RulePiPointValues: slicedValues,
        };
        return this.RuleTester(chunkBody);
      })
      .filter((req): req is Observable<any> => req !== null);

    if (chunkRequests.length === 0) {
      return of({
        evaluations: [],
        message: 'No data available for evaluation',
        error_detail: null,
      });
    }

    // Execute all chunk requests in parallel and merge results
    return forkJoin(chunkRequests).pipe(
      map((responses) => this.mergeRuleTesterResults(responses)),
    );
  }

  /**
   * Filters RulePiPointValues to only include timestamps (and their corresponding values)
   * that fall within [fromDate, toDate]. This keeps each chunk request payload small.
   */
  private sliceRulePiPointValues(
    rulePiPointValues: any,
    fromDate: Date,
    toDate: Date,
  ): any {
    if (!rulePiPointValues?.ts?.length) {
      return rulePiPointValues;
    }

    const fromMs = fromDate.getTime();
    const toMs = toDate.getTime();

    const indices: number[] = [];
    for (let i = 0; i < rulePiPointValues.ts.length; i++) {
      const tsMs = new Date(rulePiPointValues.ts[i]).getTime();
      if (tsMs >= fromMs && tsMs <= toMs) {
        const allHaveValues = rulePiPointValues.pi_point_values.every(
          (piPoint: any) => {
            if (piPoint.is_num) {
              const v = piPoint.num_values?.[i];
              return v !== null && v !== undefined;
            } else {
              const v = piPoint.str_values?.[i];
              return v !== null && v !== undefined;
            }
          },
        );
        if (allHaveValues) {
          indices.push(i);
        }
      }
    }

    const slicedTs = indices.map((i) => rulePiPointValues.ts[i]);
    const slicedPiPointValues = rulePiPointValues.pi_point_values.map(
      (piPoint: any) => ({
        ...piPoint,
        num_values: piPoint.num_values?.length
          ? indices.map((i) => piPoint.num_values[i])
          : [],
        str_values: piPoint.str_values?.length
          ? indices.map((i) => piPoint.str_values[i])
          : [],
      }),
    );

    return {
      ...rulePiPointValues,
      ts: slicedTs,
      pi_point_values: slicedPiPointValues,
    };
  }

  /**
   * Splits a date range into chunks of at most 2 weeks (14 days).
   * Uses small chunks to keep Lambda response sizes manageable and avoid 413 errors.
   */
  private splitDateRangeForRuleTester(
    fromDate: Date,
    toDate: Date,
  ): DateChunk[] {
    const CHUNK_DAYS = 14;
    const chunkMs = CHUNK_DAYS * 24 * 60 * 60 * 1000;

    const thresholdDate = new Date(fromDate.getTime() + chunkMs);

    if (thresholdDate >= toDate) {
      return [{ from: new Date(fromDate), to: new Date(toDate) }];
    }

    const chunks: DateChunk[] = [];
    let currentFrom = new Date(fromDate);

    while (currentFrom < toDate) {
      const currentTo = new Date(currentFrom.getTime() + chunkMs);

      if (currentTo >= toDate) {
        chunks.push({ from: new Date(currentFrom), to: new Date(toDate) });
        break;
      } else {
        chunks.push({ from: new Date(currentFrom), to: new Date(currentTo) });
      }

      currentFrom = new Date(currentTo);
    }

    return chunks;
  }

  /**
   * Merges multiple rule tester responses into a single response.
   * Combines evaluations arrays in chronological order.
   */
  private mergeRuleTesterResults(responses: any[]): any {
    if (responses.length === 0) {
      return { evaluations: [], message: '', error_detail: null };
    }

    if (responses.length === 1) {
      return responses[0];
    }

    // Check if any response has an error
    const errorResponse = responses.find(
      (r) => r.error_detail || (r.evaluations?.length === 0 && r.error_detail),
    );
    if (errorResponse) {
      return errorResponse;
    }

    // Merge all evaluations
    const allEvaluations: any[] = [];
    for (const response of responses) {
      if (response.evaluations && Array.isArray(response.evaluations)) {
        allEvaluations.push(...response.evaluations);
      }
    }

    // Sort by timestamp to maintain chronological order
    allEvaluations.sort((a, b) => {
      const timeA = new Date(a.iso_date_time).getTime();
      const timeB = new Date(b.iso_date_time).getTime();
      return timeA - timeB;
    });

    return {
      evaluations: allEvaluations,
      message: responses[0].message || 'Rule evaluation completed successfully',
      error_detail: null,
    };
  }
}
